import React from 'react';
import { Link } from 'react-router-dom';
import BlueCallToAction from '../components/common/BlueCallToAction';

/**
 * FeaturesPage component
 * Showcases key features and capabilities of the platform
 */
const FeaturesPage = () => {
  const features = [
    {
      title: "Revenue Cycle Insights",
      description: "Provides your organization industry-leading visibility into your KPIs. Understand performance from multiple vantage points and translate insights into action.",
      icon: "📊"
    },
    {
      title: "Verbal Order Authentication",
      description: "Solves the physician's administrative burden with automated processing and validation of verbal orders.",
      icon: "🗣️"
    },
    {
      title: "Claims Management",
      description: "Streamline claims processing with AI-powered validation and automated workflows to reduce denials and accelerate reimbursement.",
      icon: "📝"
    },
    {
      title: "Data Analytics",
      description: "Advanced analytics capabilities to predict outcomes and identify trends in your healthcare data.",
      icon: "📈"
    },
    {
      title: "Compliance Monitoring",
      description: "Automated compliance checks and real-time monitoring to ensure adherence to healthcare regulations.",
      icon: "✓"
    },
    {
      title: "Workflow Automation",
      description: "Intelligent automation of routine tasks and processes to improve efficiency and reduce administrative burden.",
      icon: "⚡"
    }
  ];

  const capabilities = [
    {
      title: "Real-time Data Processing",
      description: "Enable data to flow in real-time through every point of care, ensuring nothing is lost in the process.",
      benefits: [
        "Instant data validation",
        "Real-time alerts and notifications",
        "Automated data synchronization"
      ]
    },
    {
      title: "AI-Powered Analytics",
      description: "Leverage advanced AI/ML models to predict outcomes and identify optimization opportunities.",
      benefits: [
        "Predictive analytics",
        "Pattern recognition",
        "Automated insights generation"
      ]
    },
    {
      title: "Secure Collaboration",
      description: "Enable teams to work together efficiently while maintaining security and compliance.",
      benefits: [
        "Role-based access control",
        "Secure data sharing",
        "Audit trail tracking"
      ]
    },
    {
      title: "Integration Capabilities",
      description: "Seamlessly connect with your existing healthcare systems and workflows.",
      benefits: [
        "EHR system integration",
        "API connectivity",
        "Custom workflow support"
      ]
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Platform <span style={{ color: '#FFC000' }}>Features</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Discover how our comprehensive suite of features can transform
              your healthcare operations and improve outcomes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/demo"
                style={{ 
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                className="hover:bg-opacity-90"
              >
                Request Demo
              </Link>
              <Link
                to="/contact"
                style={{ 
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                className="hover:bg-[#0E3056] hover:text-white"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Core Features
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Capabilities Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Advanced Capabilities
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {capabilities.map((capability, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {capability.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {capability.description}
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                  {capability.benefits.map((benefit, idx) => (
                    <li key={idx}>{benefit}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Ready to explore our features?"
        description="Schedule a demo to see our platform in action."
        primaryButtonText="Request Demo"
        primaryButtonLink="/demo"
        secondaryButtonText="Contact Us"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default FeaturesPage;